import React from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiCreditCard,
  FiDatabase,
  FiExternalLink,
  FiFileText,
  FiMapPin,
  FiPackage,
  FiTruck,
} from "react-icons/fi";
import {
  typography,
  spacing,
  effects,
  components,
  gradients,
} from "../styles/designSystem";
import { testimonials } from "../data/testimonialsData";

const flowSteps = [
  {
    number: "01",
    title: "Choose route",
    text: "Country, route, date, and shipment options.",
  },
  {
    number: "02",
    title: "Build shipment",
    text: "Package type, weight, dimensions, and DHL add-ons.",
  },
  {
    number: "03",
    title: "Checkout",
    text: "Addresses, payment proof, terms, and total price.",
  },
  {
    number: "04",
    title: "Operations",
    text: "Notion records, PDF confirmation, and customer email.",
  },
];

const featureCards = [
  {
    icon: FiTruck,
    eyebrow: "Shipment logic",
    title: "Built for cross-border delivery",
    text: "Route-based shipping, multiple shipments per order, dynamic route dates, DHL add-ons, customs fees, and configurable dimensions.",
    className: "md:col-span-2",
  },
  {
    icon: FiCreditCard,
    eyebrow: "Checkout",
    title: "Clear from start to finish",
    text: "A guided multi-step checkout for buyer details, addresses, payment proof, and terms acceptance.",
  },
  {
    icon: FiFileText,
    eyebrow: "Confirmation",
    title: "PDF + email, automatically",
    text: "Every successful order creates a downloadable confirmation and sends it directly to the customer.",
  },
  {
    icon: FiDatabase,
    eyebrow: "Operations",
    title: "Notion as the control center",
    text: "Orders, shipments, payments, incoming goods, and local delivery are created automatically.",
    className: "md:col-span-2",
  },
];

const technologies = [
  "React",
  "Tailwind CSS",
  "Notion API",
  "PDF workflows",
  "Email automation",
  "Responsive design",
];

const heroHighlights = [
  "Route-based pricing",
  "Multi-step checkout",
  "Automated documents",
];

const operationItems = [
  "Order records",
  "Shipment details",
  "Payment proof",
  "Incoming goods",
  "Local delivery",
  "Customer confirmation",
];

const projectScreenshots = [
  {
    src: "/images/indoeropa/indoeropa-shipping.png",
    alt: "INDO EROPA Shipping page",
    title: "Shipping",
    description:
      "Selecting a shipping configuration and country-specific routes.",
  },
  {
    src: "/images/indoeropa/indoeropa-shipping-config.png",
    alt: "INDO EROPA Shipping configuration",
    title: "Shipping configuration",
    description: "Configure item types, item weights, shipment dates.",
  },
  {
    src: "/images/indoeropa/indoeropa-cart.png",
    alt: "INDO EROPA Cart page",
    title: "Cart",
    description: "Cart items selection.",
  },
  {
    src: "/images/indoeropa/indoeropa-checkout.png",
    alt: "INDO EROPA Checkout page",
    title: "Checkout",
    description: "Checkout form with payment proof and terms acceptance.",
  },
];

const testimonialVideo = null;
// Later:
// const testimonialVideo = "/indoeropa-testimonial.mp4";

const IndoEropaProjectPage = () => {
  const { ref: heroRef, inView: heroInView } = useInView({
    triggerOnce: true,
    threshold: 0.15,
  });

  const { ref: systemRef, inView: systemInView } = useInView({
    triggerOnce: true,
    threshold: 0.15,
  });

  const { ref: featuresRef, inView: featuresInView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
  });

  const { ref: operationsRef, inView: operationsInView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
  });

  const { ref: screenshotsRef, inView: screenshotsInView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
  });

  const projectTestimonial = testimonials.find(
    (testimonial) => testimonial.id === "indo-eropa",
  );

  const reveal = (visible) =>
    `transition-all duration-700 ease-out ${
      visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
    }`;

  return (
    <main className="relative min-h-screen overflow-hidden bg-white pt-24 md:pt-32">
      <style>{`
                @keyframes indoFloat {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    50% { transform: translateY(-18px) rotate(5deg); }
                }

                @keyframes indoDrift {
                    0%, 100% { transform: translate(0, 0); }
                    50% { transform: translate(18px, -12px); }
                }

                @keyframes indoPulse {
                    0%, 100% { transform: scale(1); opacity: 0.75; }
                    50% { transform: scale(1.08); opacity: 1; }
                }

                .indo-float {
                    animation: indoFloat 7s ease-in-out infinite;
                }

                .indo-drift {
                    animation: indoDrift 9s ease-in-out infinite;
                }

                .indo-pulse {
                    animation: indoPulse 4s ease-in-out infinite;
                }

                @media (prefers-reduced-motion: reduce) {
                    .indo-float,
                    .indo-drift,
                    .indo-pulse {
                        animation: none;
                    }
                }
            `}</style>

      {/* Decorative background */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-primary/[0.03]" />

        <div className="indo-drift absolute -right-24 top-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="indo-float absolute -left-20 top-[38%] h-52 w-52 rounded-[3rem] border border-accent/30 bg-accent/10 blur-[1px]" />
        <div className="absolute bottom-20 right-[8%] h-28 w-28 rotate-12 rounded-3xl border border-primary/20 bg-primary/[0.04]" />
        <div className="absolute left-[12%] top-[18%] h-4 w-4 rounded-full bg-primary/40" />
        <div className="absolute right-[18%] top-[52%] h-6 w-6 rounded-full border-4 border-accent/30" />
      </div>

      <section
        className={`${spacing.container} ${spacing.section} relative z-10`}
      >
        <Link
          to="/projects"
          className="mb-12 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-primary md:mb-16"
        >
          <FiArrowLeft />
          Back to projects
        </Link>

        {/* Hero */}
        <section
          ref={heroRef}
          className="grid items-center gap-14 lg:grid-cols-[0.9fr,1.1fr] lg:gap-20"
        >
          <div className={reveal(heroInView)}>
            <p className={`${typography.label} text-primary`}>
              Digital Platform
            </p>

            <h1 className={`${typography.heading.h1} mt-5 text-secondary`}>
              INDO EROPA
            </h1>

            <p className={`${typography.body.large} mt-6 max-w-xl`}>
              A custom platform that connects cross-border shopping, shipping
              logic, checkout, and day-to-day operations between Germany and
              Indonesia.
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {[
                "Route-based shipping",
                "Dynamic pricing",
                "Notion backend",
                "PDF + email workflows",
              ].map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-primary/15 bg-primary/[0.06] px-3 py-1.5 text-xs font-medium text-primary"
                >
                  {item}
                </span>
              ))}
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {heroHighlights.map((item, index) => (
                <div
                  key={item}
                  className="flex items-center gap-2 rounded-2xl border border-gray-200/70 bg-white/80 px-4 py-3 text-sm font-medium text-secondary shadow-sm backdrop-blur-sm"
                  style={{
                    transitionDelay: `${index * 80}ms`,
                  }}
                >
                  <FiCheckCircle className="shrink-0 text-primary" />
                  {item}
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href="https://indoeropa.com"
                target="_blank"
                rel="noreferrer"
                className={components.button.primary}
              >
                Visit INDO EROPA
                <FiExternalLink />
              </a>

              <a
                href="#system"
                className={`${components.button.secondary} px-7 py-4`}
              >
                See how it works
                <FiArrowRight />
              </a>
            </div>
          </div>

          <div
            className={`${reveal(heroInView)} relative`}
            style={{ transitionDelay: "140ms" }}
          >
            <div className="indo-float absolute -right-6 -top-8 h-20 w-20 rounded-[1.6rem] bg-accent/20" />
            <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full border-[10px] border-primary/10" />

            <div className="absolute -left-4 top-10 z-20 hidden rounded-2xl border border-gray-200/70 bg-white/90 px-4 py-3 shadow-xl backdrop-blur-md md:block">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FiMapPin />
                </div>
                <div>
                  <p className="text-xs font-semibold text-secondary">
                    Germany → Indonesia
                  </p>
                  <p className="text-[11px] text-gray-500">
                    Dynamic route logic
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute -right-4 bottom-12 z-20 hidden rounded-2xl border border-gray-200/70 bg-white/90 px-4 py-3 shadow-xl backdrop-blur-md md:block">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/20 text-primary">
                  <FiPackage />
                </div>
                <div>
                  <p className="text-xs font-semibold text-secondary">
                    Multi-shipment cart
                  </p>
                  <p className="text-[11px] text-gray-500">
                    Built for real orders
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`relative rounded-[2rem] border border-gray-200/70 p-3 shadow-2xl ${gradients.subtle}`}
            >
              <div className="overflow-hidden rounded-[1.35rem] bg-white">
                <div className="flex items-center gap-2 bg-gray-100 px-4 py-3">
                  <span className="h-3 w-3 rounded-full bg-red-400" />
                  <span className="h-3 w-3 rounded-full bg-yellow-400" />
                  <span className="h-3 w-3 rounded-full bg-green-400" />
                  <span className="ml-3 text-xs text-gray-500">
                    indoeropa.com
                  </span>
                </div>

                <img
                  src="/images/indoeropa/indoeropa-preview.png"
                  alt="INDO EROPA website preview"
                  className="h-[320px] w-full object-cover object-top md:h-[520px]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* System flow */}
        <section
          id="system"
          ref={systemRef}
          className="relative mt-28 overflow-hidden rounded-[2rem] bg-secondary px-7 py-12 text-white shadow-2xl md:mt-36 md:px-12 md:py-16"
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 left-[35%] h-56 w-56 rounded-full bg-accent/20 blur-3xl" />

          <div className={`relative ${reveal(systemInView)}`}>
            <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
              <div className="max-w-2xl">
                <p className={`${typography.label} text-primary`}>
                  One connected system
                </p>

                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                  From customer order to internal operations.
                </h2>

                <p className="mt-5 max-w-xl text-sm leading-7 text-white/70 md:text-base">
                  Instead of handling shipment data, payment proof, pricing,
                  documents, and fulfilment in separate tools, the platform
                  brings them into one connected flow.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.06] px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50">
                  Flow type
                </p>
                <p className="mt-2 text-sm font-semibold text-white">
                  Customer journey + backend automation
                </p>
              </div>
            </div>

            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {flowSteps.map((step, index) => (
                <article
                  key={step.number}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:bg-white/[0.11]"
                  style={{
                    transitionDelay: `${index * 80}ms`,
                  }}
                >
                  {index < flowSteps.length - 1 && (
                    <div className="absolute -right-3 top-1/2 hidden h-px w-6 bg-white/20 lg:block" />
                  )}

                  <span className="text-xs font-semibold text-primary">
                    {step.number}
                  </span>

                  <h3 className="mt-4 text-lg font-semibold">{step.title}</h3>

                  <p className="mt-2 text-sm leading-6 text-white/65">
                    {step.text}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Bento features */}
        <section ref={featuresRef} className="mt-28 md:mt-36">
          <div className={reveal(featuresInView)}>
            <p className={`${typography.label} text-primary`}>
              Built around real operations
            </p>

            <div className="mt-4 flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <h2
                className={`${typography.heading.h2} max-w-2xl text-secondary`}
              >
                More than a website — a practical operating system.
              </h2>

              <p className={`${typography.body.base} max-w-md text-gray-600`}>
                Important functions are grouped into a few connected systems
                instead of scattered pages.
              </p>
            </div>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {featureCards.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <article
                  key={feature.title}
                  className={`${components.card.base} ${
                    feature.className || ""
                  } group relative overflow-hidden p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl md:p-8`}
                  style={{
                    transitionDelay: `${index * 80}ms`,
                  }}
                >
                  <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary/[0.06] transition-transform duration-500 group-hover:scale-150" />

                  <div className="relative">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-transform duration-300 group-hover:rotate-6">
                      <Icon size={20} />
                    </div>

                    <p className={`${typography.label} mt-6 text-primary`}>
                      {feature.eyebrow}
                    </p>

                    <h3 className="mt-3 text-xl font-semibold text-secondary">
                      {feature.title}
                    </h3>

                    <p
                      className={`${typography.body.small} mt-3 max-w-xl text-gray-600`}
                    >
                      {feature.text}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Operations + editable data */}
        <section
          ref={operationsRef}
          className="mt-28 grid gap-8 md:mt-36 lg:grid-cols-[0.9fr,1.1fr]"
        >
          <div
            className={`${reveal(
              operationsInView,
            )} relative overflow-hidden rounded-[2rem] bg-primary p-8 text-white shadow-xl md:p-10`}
          >
            <div className="absolute -bottom-14 -right-10 h-44 w-44 rounded-full border-[18px] border-white/10" />
            <div className="indo-pulse absolute right-10 top-10 h-3 w-3 rounded-full bg-accent" />

            <div className="relative">
              <FiDatabase size={26} className="text-white/80" />

              <p className="mt-7 text-sm font-semibold uppercase tracking-[0.18em] text-white/65">
                Flexible operations
              </p>

              <h2 className="mt-4 text-3xl font-semibold leading-tight">
                Update everyday data without changing code.
              </h2>

              <p className="mt-5 max-w-md text-sm leading-7 text-white/75">
                Routes, package types, pricing, DHL tiers, dates, dimensions,
                and EUR to IDR rates remain editable through Notion.
              </p>
            </div>
          </div>

          <div
            className={`${components.card.base} ${reveal(
              operationsInView,
            )} relative overflow-hidden p-8 md:p-10`}
            style={{ transitionDelay: "120ms" }}
          >
            <div className="absolute right-8 top-8 h-14 w-14 rotate-12 rounded-2xl bg-accent/15" />

            <p className={`${typography.label} text-primary`}>
              Live operational records
            </p>

            <h2 className={`${typography.heading.h2} mt-4 text-secondary`}>
              Everything lands in the right place.
            </h2>

            <p
              className={`${typography.body.base} mt-4 max-w-xl text-gray-600`}
            >
              After checkout, the platform creates structured records for
              fulfilment, payment tracking, and local delivery.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {operationItems.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 rounded-2xl border border-gray-200/70 bg-gray-50 px-4 py-3 text-sm font-medium text-secondary"
                >
                  <FiCheckCircle className="shrink-0 text-primary" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshots */}
        {projectScreenshots.length > 0 && (
          <section ref={screenshotsRef} className="mt-28 md:mt-36">
            <div className={reveal(screenshotsInView)}>
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div>
                  <p className={`${typography.label} text-primary`}>
                    Platform preview
                  </p>
                  <h2
                    className={`${typography.heading.h2} mt-4 text-secondary`}
                  >
                    Screens from the actual workflow.
                  </h2>
                </div>

                <p
                  className={`${typography.body.small} max-w-sm text-gray-600`}
                >
                  Selected examples from checkout and internal operations.
                </p>
              </div>
            </div>

            <div className="mt-12 grid gap-8 md:grid-cols-2">
              {projectScreenshots.map((screenshot, index) => (
                <article
                  key={screenshot.src}
                  className={`${components.card.base} group overflow-hidden p-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
                  style={{
                    transitionDelay: `${index * 80}ms`,
                  }}
                >
                  <div className="overflow-hidden rounded-2xl bg-gray-100">
                    <img
                      src={screenshot.src}
                      alt={screenshot.alt}
                      className="h-[300px] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03] md:h-[340px]"
                    />
                  </div>

                  <div className="px-3 pb-4 pt-5">
                    <h3 className="text-lg font-semibold text-secondary">
                      {screenshot.title}
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-gray-600">
                      {screenshot.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Testimonial */}
        {projectTestimonial && (
          <section className="mt-28 md:mt-36">
            <div
              className={`${effects.border.lightHeavy} relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-primary/[0.08] via-white to-accent/[0.12] p-8 md:p-12`}
            >
              <div
                className="pointer-events-none absolute -left-8 top-0 h-24 w-24 rounded-full bg-primary/10 blur-2xl"
                aria-hidden="true"
              />

              <div
                className="pointer-events-none absolute -bottom-14 right-10 h-44 w-44 rounded-full bg-accent/20 blur-3xl"
                aria-hidden="true"
              />

              <div
                className={`relative grid items-center gap-10 ${
                  testimonialVideo ? "lg:grid-cols-2" : "mx-auto max-w-3xl"
                }`}
              >
                <div>
                  <div
                    className="text-5xl leading-none text-primary"
                    aria-hidden="true"
                  >
                    &ldquo;
                  </div>

                  <blockquote
                    className={`${typography.body.base} mt-5 italic leading-8 text-gray-700`}
                  >
                    {projectTestimonial.quote}
                  </blockquote>

                  <div className="mt-7 flex items-center gap-4 border-t border-gray-200/70 pt-6">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent text-sm font-bold text-white shadow-md">
                      {projectTestimonial.initials}
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-secondary">
                        {projectTestimonial.name}
                      </p>

                      <p className={`${typography.body.xs} text-gray-500`}>
                        {projectTestimonial.role}
                        {projectTestimonial.company
                          ? `, ${projectTestimonial.company}`
                          : ""}
                      </p>
                    </div>
                  </div>
                </div>

                {testimonialVideo && (
                  <video
                    controls
                    preload="metadata"
                    className="aspect-video w-full rounded-2xl border border-white/60 object-cover shadow-xl"
                  >
                    <source src={testimonialVideo} type="video/mp4" />
                    Your browser does not support the video element.
                  </video>
                )}
              </div>
            </div>
          </section>
        )}

        {/* CTA + technology */}
        <section className="mt-28 pb-8 text-center md:mt-36">
          <div className="mx-auto max-w-2xl">
            <p className={`${typography.label} text-primary`}>Technology</p>

            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {technologies.map((technology) => (
                <span
                  key={technology}
                  className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700"
                >
                  {technology}
                </span>
              ))}
            </div>

            <h2 className={`${typography.heading.h2} mt-14 text-secondary`}>
              Need a platform built around your workflow?
            </h2>

            <p className={`${typography.body.base} mx-auto mt-5 max-w-xl`}>
              We build connected digital systems that help businesses simplify
              customer journeys and internal operations.
            </p>

            <Link
              to="/contact"
              className={`${components.button.primary} mx-auto mt-9`}
            >
              Get in touch
              <FiArrowRight />
            </Link>
          </div>
        </section>
      </section>
    </main>
  );
};

export default IndoEropaProjectPage;
